import React, { useMemo } from 'react';
import { Tooltip, Progress, Space, Typography } from 'antd';
import { ExclamationCircleOutlined, WarningOutlined, CheckCircleOutlined, EuroCircleOutlined } from '@ant-design/icons';
import { parseNumberLike } from './utils';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const { Text } = Typography;

/**
 * ServiziUsage
 * Compact and expanded usage indicator for org services versus purchased limit.
 * Props:
 * - org: {
 *     nome,
 *     limite_servizi,
 *     total_services_current_billing_period,
 *     zoho_last_billing,
 *     zoho_next_billing
 *   }
 * - mode: 'compact' | 'expanded'
 */
export default function ServiziUsage({ org: _org, stats = null, mode = 'compact' }) {

  const { limit, used, periodStart, periodEnd, pctUsed, pctProjected, expectedTotal, status, hint, legacyUnlimited } = useMemo(() => {
    const s = stats || {};
    const legacyUnlimited = !!s.limit_is_flat;
    const limitNum = legacyUnlimited ? null : parseNumberLike(s.purchased_amount);
    const usedNum = parseNumberLike(s.total_count);
    const periodStart = s.period_start ? new Date(s.period_start) : null;
    const periodEnd = s.period_end ? new Date(s.period_end) : null;
    const pctUsed = parseNumberLike(s.pct_used);
    const pctProjected = parseNumberLike(s.pct_projected);
    const expectedTotal = parseNumberLike(s.expected_total);
    const needsMore = !!s.needs_more_data;
    const usageState = String(s.usage_state || '').toLowerCase();

    let status = 'unknown';
    let hint = '';
    if (needsMore) {
      status = 'needs_more';
      hint = 'Ho bisogno di più dati';
    } else if (legacyUnlimited) {
      status = 'unlimited';
      hint = 'Piano FLAT';
    } else {
      switch (usageState) {
        case 'upsell':
          status = 'upsell';
          hint = 'Opportunità di upsell';
          break;
        case 'churn_risk':
          status = 'churn';
          hint = 'Rischio abbandono/downgrade';
          break;
        case 'behind':
          status = 'behind';
          hint = 'In ritardo rispetto all\'atteso';
          break;
        case 'ok':
          status = 'ok';
          hint = 'Nei limiti';
          break;
        case 'legacy_flat':
          status = 'unlimited';
          hint = 'Piano FLAT';
          break;
        default:
          status = 'ok';
          hint = 'Nei limiti';
      }
    }

    const out = { limit: limitNum, used: usedNum, periodStart, periodEnd, pctUsed, pctProjected, expectedTotal, status, hint, legacyUnlimited };
    return out;
  }, [stats]);

  if (!stats) return null;

  // Visual styles
  const iconMap = {
    ok: <CheckCircleOutlined style={{ color: '#3D5864' }} />,
    behind: <WarningOutlined style={{ color: '#fa8c16' }} />,
    churn: <ExclamationCircleOutlined style={{ color: '#f5222d' }} />,
    upsell: <EuroCircleOutlined style={{ color: '#52c41a' }} />,
    unlimited: <CheckCircleOutlined style={{ color: '#3D5864' }} />,
    unknown: <ExclamationCircleOutlined />,
  };

  if (mode === 'compact') {
    if (legacyUnlimited) {
      const endRel = periodEnd ? formatDistanceToNow(periodEnd, { addSuffix: true, locale: it }) : null;
      const tip = (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Space>
              {iconMap['unlimited']}
              <Text strong>Piano legacy FLAT</Text>
            </Space>
          </div>
          <div>
            <Text>Utilizzo: {used != null ? used : '-'} / FLAT</Text>
          </div>
          {periodStart && periodEnd && (
            <div>
              <Text type="secondary">
                Periodo: {periodStart.toLocaleDateString('it-IT')} → {periodEnd.toLocaleDateString('it-IT')} {endRel ? `(fine ${endRel})` : ''}
              </Text>
            </div>
          )}
        </div>
      );

      return (
        <Tooltip title={tip} placement="top" color="#fff" overlayInnerStyle={{ color: '#000', border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ minWidth: 120 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span>{used != null ? used : '-'}/FLAT</span>
              <span>{iconMap['unlimited']}</span>
            </div>
          </div>
        </Tooltip>
      );
    }
    const percent = typeof pctUsed === 'number' ? Math.max(0, Math.min(100, Math.round(pctUsed))) : 0;
    // Bar color logic:
    // - Red when over limit or projection > 130%
    // - Orange when projection < 80% or status is 'trending-over'
    // - Default blue otherwise
    // Map to new palette based on projection
    const strokeColor = (function() {
      if (typeof pctProjected === 'number') {
        if (pctProjected > 100 || (used != null && limit != null && used > limit)) return '#52c41a'; // green upsell
        if (pctProjected < 50) return '#f5222d'; // red churn
        if (pctProjected < 80) return '#fa8c16'; // orange behind
      }
      return '#3D5864'; // ok
    })();

    const projColor = (function() {
      if (typeof pctProjected !== 'number') return null;
      if (pctProjected > 100 || (used != null && limit != null && used > limit)) return '#52c41a'; // green upsell
      if (pctProjected < 50) return '#f5222d'; // red churn risk
      if (pctProjected < 80) return '#fa8c16'; // orange behind
      return '#3D5864'; // blue-ish default
    })();

    const projState = (function() {
      if (typeof pctProjected !== 'number') return null;
      if (pctProjected > 100 || (used != null && limit != null && used > limit)) return 'over';
      if (pctProjected < 50) return 'churn';
      if (pctProjected < 80) return 'behind';
      return 'ok';
    })();

    const endRel = periodEnd ? formatDistanceToNow(periodEnd, { addSuffix: true, locale: it }) : null;
    const tip = (
      <div>
        <div style={{ marginBottom: 4 }}>
          <Space>
            {iconMap[status]}
            <Text strong>{hint}</Text>
          </Space>
        </div>
        <div>
          <Text>Utilizzo: {used != null ? used : '-'} su {limit != null ? limit : '-'}</Text>
        </div>
        {periodStart && periodEnd && (
          <div>
            <Text type="secondary">
              Periodo: {periodStart.toLocaleDateString('it-IT')} → {periodEnd.toLocaleDateString('it-IT')} {endRel ? `(fine ${endRel})` : ''}
            </Text>
          </div>
        )}
        {typeof pctProjected === 'number' && (
          <div>
            <Text style={{ color: projColor }}>
              Proiezione: {pctProjected}%{expectedTotal != null ? ` (previsto: ${expectedTotal})` : ''} — {
                (pctProjected > 100 || (used != null && limit != null && used > limit)) ? 'oltre il limite — opportunità di upselling' :
                (pctProjected < 50) ? 'utilizzo molto basso (rischio abbandono)' :
                (pctProjected < 80) ? 'in ritardo rispetto all\'atteso' :
                'nei limiti'
              }
            </Text>
          </div>
        )}
      </div>
    );

    return (
      <Tooltip
        title={tip}
        placement="top"
        color="#fff"
        overlayInnerStyle={{ color: '#000', border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <div style={{ minWidth: 120 }}>
          <Progress percent={percent} size="small" showInfo={false} strokeColor={strokeColor} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>{used != null ? used : '-'}/{limit != null ? limit : '-'}</span>
            {typeof pctProjected === 'number' && (
              <span>
                {projState === 'over' && <EuroCircleOutlined style={{ color: '#52c41a' }} />}
                {projState === 'churn' && <ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
                {projState === 'behind' && <WarningOutlined style={{ color: '#fa8c16' }} />}
                {projState === 'ok' && <CheckCircleOutlined style={{ color: '#3D5864' }} />}
              </span>
            )}
          </div>
        </div>
      </Tooltip>
    );
  }

  // Expanded mode
  const endRel = periodEnd ? formatDistanceToNow(periodEnd, { addSuffix: true, locale: it }) : null;
  const projColorExpanded = (function() {
    if (typeof pctProjected !== 'number') return null;
    if (pctProjected > 100 || (used != null && limit != null && used > limit)) return '#52c41a';
    if (pctProjected < 50) return '#f5222d';
    if (pctProjected < 80) return '#fa8c16';
    return '#3D5864';
  })();

  return (
    <div>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space align="center">
          {iconMap[status]}
          <Text strong>{hint}</Text>
        </Space>
        <div>
          {!legacyUnlimited && (
            <Progress
              percent={typeof pctUsed === 'number' ? pctUsed : 0}
              strokeColor={
                (typeof pctProjected === 'number')
                  ? ((pctProjected > 100 || (used != null && limit != null && used > limit)) ? '#52c41a'
                    : (pctProjected < 50 ? '#f5222d' : (pctProjected < 80 ? '#fa8c16' : undefined)))
                  : undefined
              }
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>
              Utilizzo: {used != null ? used : '-'} {legacyUnlimited ? '/ FLAT' : `su ${limit != null ? limit : '-'}`}
            </Text>
            {!legacyUnlimited && typeof pctProjected === 'number' && (
              <span>
                {(pctProjected > 100 || (used != null && limit != null && used > limit)) && <EuroCircleOutlined style={{ color: '#52c41a' }} />}
                {(pctProjected < 50) && <ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
                {(pctProjected >= 50 && pctProjected < 80) && <WarningOutlined style={{ color: '#fa8c16' }} />}
                {(pctProjected >= 80 && pctProjected <= 100) && <CheckCircleOutlined style={{ color: '#3D5864' }} />}
              </span>
            )}
          </div>
        </div>
        {periodStart && periodEnd && (
          <Text type="secondary">
            Periodo: {periodStart.toLocaleDateString('it-IT')} → {periodEnd.toLocaleDateString('it-IT')} {endRel ? `(fine ${endRel})` : ''}
          </Text>
        )}
        {!legacyUnlimited && typeof pctProjected === 'number' && (
          <Text style={{ color: projColorExpanded }}>
            Proiezione: {pctProjected}%{expectedTotal != null ? ` (previsto: ${expectedTotal})` : ''} — {
              (pctProjected > 100 || (used != null && limit != null && used > limit)) ? 'oltre il limite — opportunità di upselling' :
              (pctProjected < 50) ? 'utilizzo molto basso (rischio abbandono)' :
              (pctProjected < 80) ? 'in ritardo rispetto all\'atteso' :
              'nei limiti'
            }
          </Text>
        )}
      </Space>
    </div>
  );
}
